import React from "react";
// Roddo Crew Dock responsive polish v2026-09-09
import type { AppUser, UserProfile, UserRelationship, ToastType } from "../types";
import { View } from "../types";
import {
  acceptFriendRequest,
  declineFriendRequest,
  getCrewMemberProfiles,
  subscribeToCrewRelationships,
  updateUserProfile,
} from "../services/users";
import {
  isCrewPresenceOptedIn,
  markOwnCrewPresenceOffline,
  subscribeToAcceptedCrewPresence,
} from "../services/crewPresence";
import { resolveRoddoDarkMode } from "../constants/theme";
import CrewBoardView from "./CrewBoardView";
import CrewDMView from "./CrewDMView";
import RoddoConfirmDialog from './RoddoConfirmDialog';
import { ShareIcon } from "./icons";
import {
  createGroupCrew,
  deleteGroupCrew,
  ensureCrewChannelFromRelationship,
  leaveGroupCrew,
  setGroupCrewMemberRole,
  subscribeGroupCrews,
  updateGroupCrewMembers,
  updateGroupCrewProfile,
  type CrewChannel,
} from "../services/crews";
import { deleteCrewCover, uploadCrewCover } from "../services/storage";
import { buildCrewShareUrl, share } from "../utils/share";
import { subscribeCrewDMInbox, type CrewDMInboxRow } from "../services/crewDMs";

type Props = {
  currentUser: UserProfile;
  users?: Record<string, AppUser>;
  navigateTo: (view: View, state?: Record<string, any>) => void;
  addToast: (message: string, type?: ToastType) => void;
  isDarkMode?: boolean;
  onBack?: () => void;
  onManageCrew?: () => void;
  directCrewBackToMap?: boolean;
  initialCrewId?: string;
  initialCrewPostId?: string;
  initialDmId?: string;
  initialDmUid?: string;
};

const DEFAULT_AVATAR = "/avatar-default.png";
const clean = (v: any) => String(v ?? "").trim();

const otherUid = (rel: UserRelationship, uid: string) => {
  const ids = Array.isArray(rel.participantIds)
    ? rel.participantIds
    : [rel.requesterId, rel.recipientId].filter(Boolean);
  return ids.find((id) => id && id !== uid) || "";
};

const displayNameFor = (user: any, _uid: string) =>
  clean(user?.displayName) ||
  clean(user?.name) ||
  clean(user?.username) ||
  clean(user?.handle) ||
  "Angler";

const avatarFor = (user: any) =>
  clean(user?.avatar) ||
  clean(user?.avatarUrl) ||
  clean(user?.photoURL) ||
  DEFAULT_AVATAR;

const hasCustomAvatar = (user: any) => {
  const avatar = avatarFor(user);
  return Boolean(avatar && avatar !== DEFAULT_AVATAR);
};

const CrewAvatar: React.FC<{
  user: any;
  name: string;
  sizeClass?: string;
  imageClassName?: string;
  ringColor?: string;
}> = ({
  user,
  name,
  sizeClass = "h-14 w-14",
  imageClassName = "",
  ringColor,
}) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const src = avatarFor(user);
  const showImage = hasCustomAvatar(user) && !imageFailed;

  return (
    <div
      className={`grid shrink-0 place-items-center overflow-hidden rounded-full border ${sizeClass}`}
      style={{
        borderColor: ringColor || "rgba(132,151,190,0.18)",
        background:
          "linear-gradient(145deg, rgba(47,128,237,.14), rgba(138,112,242,.10))",
      }}
      aria-hidden="true"
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          onError={() => setImageFailed(true)}
          className={`h-full w-full object-cover ${imageClassName}`}
        />
      ) : (
        <span className="text-[12px] font-black tracking-[0.04em] opacity-80">
          {initialsFor(name)}
        </span>
      )}
    </div>
  );
};

const initialsFor = (name: string) => {
  const value = clean(name);
  if (!value || value === "Angler") return "FC";

  return (
    value
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase() || "FC"
  );
};

const crewCoverUrl = (crew: Partial<CrewChannel> | null | undefined, thumb = false) =>
  clean(thumb ? (crew as any)?.coverThumbUrl || (crew as any)?.coverImageUrl : (crew as any)?.coverImageUrl || (crew as any)?.coverThumbUrl);

const crewCoverStyle = (crew: Partial<CrewChannel> | null | undefined): React.CSSProperties => {
  const x = Math.min(100, Math.max(0, Number((crew as any)?.coverImagePositionX ?? 50)));
  const y = Math.min(100, Math.max(0, Number((crew as any)?.coverImagePositionY ?? 50)));
  const zoom = Math.min(1.6, Math.max(1, Number((crew as any)?.coverImageZoom || 1)));
  return {
    objectPosition: `${x}% ${y}%`,
    transform: `scale(${zoom})`,
    transformOrigin: `${x}% ${y}%`,
  };
};

type SettingsScreen = "overview" | "members" | "add";

const CrewPageShell: React.FC<{
  children: React.ReactNode;
  color: string;
  isDarkMode: boolean;
}> = ({ children, color, isDarkMode }) => (
  <div
    data-roddo-crew-polish
    data-roddo-tour="crew-page"
    style={{
      color,
      ["--crew-focus" as any]: isDarkMode ? "#7657E8" : "#2F80ED",
    }}
    className="mx-auto flex w-full max-w-3xl lg:max-w-5xl flex-col gap-4 px-3 sm:px-4 lg:px-6 pb-28 pt-3 lg:pt-5"
  >
    <style>{crewTactilePolishStyles}</style>
    {children}
  </div>
);

const crewTactilePolishStyles = `
  [data-roddo-crew-polish] button,
  [data-roddo-crew-polish] [role="button"],
  [data-roddo-crew-polish] input,
  [data-roddo-crew-polish] textarea {
    -webkit-tap-highlight-color: transparent;
  }

  [data-roddo-crew-polish] button,
  [data-roddo-crew-polish] [role="button"] {
    transform: translateZ(0);
    transition:
      transform 180ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 180ms cubic-bezier(0.22, 1, 0.36, 1),
      box-shadow 220ms cubic-bezier(0.22, 1, 0.36, 1),
      border-color 220ms ease,
      background 220ms ease;
  }

  [data-roddo-crew-polish] button:hover,
  [data-roddo-crew-polish] [role="button"]:hover {
    transform: translateY(-1px) translateZ(0);
    filter: brightness(1.025) saturate(1.03);
  }

  [data-roddo-crew-polish] button:active,
  [data-roddo-crew-polish] [role="button"]:active {
    transform: translateY(1px) scale(0.985) translateZ(0);
  }

  [data-roddo-crew-polish] input:focus-visible,
  [data-roddo-crew-polish] textarea:focus-visible,
  [data-roddo-crew-polish] button:focus-visible {
    outline: none;
    box-shadow:
      0 0 0 3px color-mix(in srgb, var(--crew-focus, #2F80ED) 22%, transparent),
      0 0 18px color-mix(in srgb, var(--crew-focus, #2F80ED) 12%, transparent);
  }

  [data-roddo-crew-polish] .crew-card {
    transition:
      transform 220ms cubic-bezier(0.22, 1, 0.36, 1),
      filter 220ms ease,
      box-shadow 260ms ease;
  }

  [data-roddo-crew-polish] .crew-card:hover {
    transform: translateY(-1px);
    filter: saturate(1.02) brightness(1.01);
  }

  @media (prefers-reduced-motion: reduce) {
    [data-roddo-crew-polish] button,
    [data-roddo-crew-polish] [role="button"],
    [data-roddo-crew-polish] .crew-card {
      transition: none !important;
      transform: none !important;
      filter: none !important;
    }
  }
`;


const CrewView: React.FC<Props> = ({
  currentUser,
  users = {},
  navigateTo,
  addToast,
  isDarkMode: explicitIsDarkMode,
  onBack,
  onManageCrew,
  directCrewBackToMap = false,
  initialCrewId,
  initialCrewPostId,
  initialDmId,
  initialDmUid,
}) => {
  const uid = clean((currentUser as any)?.uid);
  const isDarkMode = resolveRoddoDarkMode(explicitIsDarkMode);

  const [relationships, setRelationships] = React.useState<UserRelationship[]>([]);
  const [groupCrews, setGroupCrews] = React.useState<CrewChannel[]>([]);
  const [onlineCrewUids, setOnlineCrewUids] = React.useState<Set<string>>(() => new Set());
  const [crewProfileFallbacks, setCrewProfileFallbacks] = React.useState<Record<string, AppUser>>({});

  const [activeGroupId, setActiveGroupId] = React.useState("");
  const [selectedBoardRelId, setSelectedBoardRelId] = React.useState("");
  const [readyCrewId, setReadyCrewId] = React.useState("");
  const [activeDmUid, setActiveDmUid] = React.useState("");
  const [dmInbox, setDmInbox] = React.useState<CrewDMInboxRow[]>([]);
  const [activeCrewMenuOpen, setActiveCrewMenuOpen] = React.useState(false);
  const activeCrewMenuRef = React.useRef<HTMLDivElement | null>(null);

  const [createCrewOpen, setCreateCrewOpen] = React.useState(false);
  const [newCrewName, setNewCrewName] = React.useState("");
  const [newCrewMemberIds, setNewCrewMemberIds] = React.useState<string[]>([]);
  const [creatingCrew, setCreatingCrew] = React.useState(false);

  const [manageGroupId, setManageGroupId] = React.useState("");
  const [settingsScreen, setSettingsScreen] = React.useState<SettingsScreen>("overview");
  const [editCrewName, setEditCrewName] = React.useState("");
  const [editCrewDescription, setEditCrewDescription] = React.useState("");
  const [coverFile, setCoverFile] = React.useState<File | null>(null);
  const [coverPreviewUrl, setCoverPreviewUrl] = React.useState<string | null>(null);
  const [coverRemoved, setCoverRemoved] = React.useState(false);
  const [coverImageZoom, setCoverImageZoom] = React.useState(1);
  const [coverImagePositionX, setCoverImagePositionX] = React.useState(50);
  const [coverImagePositionY, setCoverImagePositionY] = React.useState(50);
  const coverDragRef = React.useRef<{
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startPositionX: number;
    startPositionY: number;
    width: number;
    height: number;
  } | null>(null);
  const [coverDragging, setCoverDragging] = React.useState(false);
  const [groupManageBusy, setGroupManageBusy] = React.useState(false);
  const [memberMenuUid, setMemberMenuUid] = React.useState("");
  const [crewConfirm, setCrewConfirm] = React.useState<
    | null
    | { kind: "removeMember"; crewId: string; memberUid: string; memberName: string; crewTitle: string }
    | { kind: "leaveCrew"; crewId: string; crewTitle: string }
    | { kind: "deleteCrew"; crewId: string; crewTitle: string }
  >(null);

  const [requestBusyId, setRequestBusyId] = React.useState<string | null>(null);
  const [isPresenceSaving, setIsPresenceSaving] = React.useState(false);
  const presenceSettings = ((currentUser as any)?.notificationSettings || {}) as Record<string, any>;
  const [presenceOptIn, setPresenceOptIn] = React.useState(isCrewPresenceOptedIn(presenceSettings));

  React.useEffect(() => {
    const settings = ((currentUser as any)?.notificationSettings || {}) as Record<string, any>;
    setPresenceOptIn(isCrewPresenceOptedIn(settings));
  }, [
    (currentUser as any)?.notificationSettings?.shareCrewMapPresence,
    (currentUser as any)?.notificationSettings?.crewMapPresence,
    (currentUser as any)?.notificationSettings?.crewMapPresenceEnabled,
  ]);

  React.useEffect(() => {
    if (!uid) return;
    return subscribeToCrewRelationships(uid, setRelationships, (err) =>
      console.warn("[CrewView] crew subscription warning", err),
    );
  }, [uid]);

  React.useEffect(() => {
    if (!uid) {
      setDmInbox([]);
      return;
    }

    // DM ids are the deterministic accepted relationship ids. Listening to the
    // exact thread docs keeps the inbox private and avoids a collection-list
    // permission query.
    const acceptedDmIds = relationships
      .filter((relationship) => String(relationship?.status || "") === "accepted")
      .map((relationship) => clean(relationship?.id))
      .filter(Boolean);

    return subscribeCrewDMInbox(
      uid,
      acceptedDmIds,
      setDmInbox,
      (err) => console.warn("[CrewView] Crew message inbox warning", err),
    );
  }, [uid, relationships]);

  React.useEffect(() => {
    if (!uid) {
      setGroupCrews([]);
      return;
    }
    return subscribeGroupCrews(uid, setGroupCrews, (err) =>
      console.warn("[CrewView] group crew subscription warning", err),
    );
  }, [uid]);

  React.useEffect(() => {
    const requestedCrewId = clean(initialCrewId);
    if (!requestedCrewId || !groupCrews.length) return;
    const match = groupCrews.find(
      (crew) =>
        crew.id === requestedCrewId &&
        crew.status !== "deleted" &&
        (crew.memberIds || []).includes(uid),
    );
    if (!match) return;
    setSelectedBoardRelId("");
    setActiveGroupId(match.id);
  }, [initialCrewId, groupCrews, uid]);

  React.useEffect(() => {
    if (!uid) {
      setOnlineCrewUids(new Set());
      return;
    }
    return subscribeToAcceptedCrewPresence(
      uid,
      (presenceList) =>
        setOnlineCrewUids(
          new Set(presenceList.map((presence) => clean((presence as any)?.uid)).filter(Boolean)),
        ),
      (err) => console.warn("[CrewView] crew presence status warning", err),
    );
  }, [uid]);

  const accepted = React.useMemo(
    () => relationships.filter((r) => r.status === "accepted"),
    [relationships],
  );
  const incoming = React.useMemo(
    () => relationships.filter((r) => r.status === "pending" && r.recipientId === uid),
    [relationships, uid],
  );

  React.useEffect(() => {
    if (!uid || !accepted.length) {
      setCrewProfileFallbacks({});
      return;
    }

    const missingUids = accepted
      .map((rel) => otherUid(rel, uid))
      .filter(Boolean)
      .filter((targetUid) => {
        const existing = users[targetUid] as any;
        return !existing || !clean(existing?.displayName || existing?.name || existing?.username || existing?.handle);
      });

    if (!missingUids.length) return;

    let cancelled = false;
    void getCrewMemberProfiles(missingUids)
      .then((profiles) => {
        if (cancelled || !profiles) return;
        setCrewProfileFallbacks((prev) => ({ ...prev, ...profiles }));
      })
      .catch((err) => {
        console.warn("[CrewView] crew profile hydration warning", err);
      });

    return () => {
      cancelled = true;
    };
  }, [uid, accepted, users]);

  const crewUserFor = React.useCallback(
    (targetUid: string) =>
      (users[targetUid] as any) ||
      (crewProfileFallbacks[targetUid] as any) ||
      null,
    [users, crewProfileFallbacks],
  );

  React.useEffect(() => {
    const requestedCrewId = clean(initialCrewId);
    if (!requestedCrewId || !accepted.length) return;
    if (groupCrews.some((crew) => crew.id === requestedCrewId)) return;
    const relationship = accepted.find((rel) => rel.id === requestedCrewId);
    if (!relationship) return;
    setActiveGroupId("");
    setSelectedBoardRelId(relationship.id);
  }, [initialCrewId, accepted, groupCrews]);

  React.useEffect(() => {
    const requestedDmId = clean(initialDmId);
    const requestedDmUid = clean(initialDmUid);
    if (!accepted.length || (!requestedDmId && !requestedDmUid)) return;

    const relationship = accepted.find((rel) => {
      const peerUid = otherUid(rel, uid);
      return (requestedDmId && rel.id === requestedDmId) || (requestedDmUid && peerUid === requestedDmUid);
    });
    if (!relationship) return;

    const peerUid = otherUid(relationship, uid);
    if (!peerUid) return;
    setActiveGroupId("");
    setSelectedBoardRelId("");
    setReadyCrewId("");
    setActiveDmUid(peerUid);
  }, [initialDmId, initialDmUid, accepted, uid]);

  const crewCards = React.useMemo(
    () => accepted.map((rel) => {
      const targetUid = otherUid(rel, uid);
      const user = targetUid ? crewUserFor(targetUid) : null;
      return {
        rel,
        uid: targetUid,
        user,
        name: displayNameFor(user, targetUid),
        avatar: avatarFor(user),
        isOnline: onlineCrewUids.has(targetUid),
      };
    }),
    [accepted, uid, crewUserFor, onlineCrewUids],
  );

  const dmInboxByUid = React.useMemo(() => {
    const map = new Map<string, CrewDMInboxRow>();
    dmInbox.forEach((row) => {
      if (row.otherUid) map.set(row.otherUid, row);
    });
    return map;
  }, [dmInbox]);

  const activeDmCard = React.useMemo(
    () => crewCards.find((card) => card.uid === activeDmUid) || null,
    [crewCards, activeDmUid],
  );

  const activeGroup = React.useMemo(
    () => groupCrews.find((crew) => crew.id === activeGroupId) || null,
    [groupCrews, activeGroupId],
  );
  const managedGroup = React.useMemo(
    () => groupCrews.find((crew) => crew.id === manageGroupId) || null,
    [groupCrews, manageGroupId],
  );

  React.useEffect(() => {
    if (!activeCrewMenuOpen) return;

    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node | null;
      if (target && activeCrewMenuRef.current?.contains(target)) return;
      setActiveCrewMenuOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setActiveCrewMenuOpen(false);
    };

    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("touchstart", onPointerDown, { passive: true });
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("touchstart", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeCrewMenuOpen]);

  const selectedBoardRelationship = React.useMemo(
    () => accepted.find((rel) => rel.id === selectedBoardRelId) || null,
    [accepted, selectedBoardRelId],
  );
  const selectedBoardUid = selectedBoardRelationship ? otherUid(selectedBoardRelationship, uid) : "";
  const selectedBoardUser = selectedBoardUid ? crewUserFor(selectedBoardUid) : null;
  const selectedBoardName = selectedBoardUid ? displayNameFor(selectedBoardUser, selectedBoardUid) : "";

  React.useEffect(() => {
    if (selectedBoardRelId && !accepted.some((rel) => rel.id === selectedBoardRelId)) {
      setSelectedBoardRelId("");
      setReadyCrewId("");
    }
  }, [accepted, selectedBoardRelId]);

  React.useEffect(() => {
    if (!activeDmUid) return;
    const stillAccepted = accepted.some((rel) => otherUid(rel, uid) === activeDmUid);
    if (!stillAccepted) setActiveDmUid("");
  }, [accepted, uid, activeDmUid]);

  React.useEffect(() => {
    if (activeGroupId && !groupCrews.some((crew) => crew.id === activeGroupId)) {
      setActiveGroupId("");
    }
  }, [groupCrews, activeGroupId]);

  React.useEffect(() => {
    if (!managedGroup) return;
    setEditCrewName(managedGroup.title || "Fishing Crew");
    setEditCrewDescription(managedGroup.description || "");
    setCoverPreviewUrl(crewCoverUrl(managedGroup) || null);
    setCoverFile(null);
    setCoverRemoved(false);
    setCoverImageZoom(Math.min(1.6, Math.max(1, Number(managedGroup.coverImageZoom || 1))));
    setCoverImagePositionX(Math.min(100, Math.max(0, Number(managedGroup.coverImagePositionX ?? 50))));
    setCoverImagePositionY(Math.min(100, Math.max(0, Number(managedGroup.coverImagePositionY ?? 50))));
  }, [managedGroup?.id, managedGroup?.title, managedGroup?.description, managedGroup?.coverImageUrl, managedGroup?.coverThumbUrl, managedGroup?.coverImageZoom, managedGroup?.coverImagePositionX, managedGroup?.coverImagePositionY]);

  React.useEffect(() => {
    return () => {
      if (coverPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(coverPreviewUrl);
    };
  }, [coverPreviewUrl]);

  React.useEffect(() => {
    let cancelled = false;
    const sync = async () => {
      if (!selectedBoardRelationship || !uid) {
        setReadyCrewId("");
        return;
      }
      try {
        const crewId = await ensureCrewChannelFromRelationship(
          selectedBoardRelationship,
          currentUser,
          selectedBoardUser,
        );
        if (!cancelled) setReadyCrewId(crewId);
      } catch (err) {
        console.warn("[CrewView] private board sync failed", err);
        if (!cancelled) addToast("Private crew board could not load yet.", "warning");
      }
    };
    void sync();
    return () => {
      cancelled = true;
    };
  }, [selectedBoardRelationship, selectedBoardUser, currentUser, uid, addToast]);

  const openProfile = (targetUid: string) => {
    if (!targetUid) return;

    navigateTo(View.PublicProfile, {
      targetUserId: targetUid,
      ...(activeGroupId ? { returnCrewId: activeGroupId } : {}),
    });
  };

  const backFromCrew = () => {
    setActiveCrewMenuOpen(false);

    // A crew opened directly from Big Blue should return to the same map route
    // instead of stepping through the generic Crew Dock first. Crews opened
    // from inside Crew Dock keep the normal in-page back behavior.
    if (directCrewBackToMap && onBack) {
      onBack();
      return;
    }

    setActiveGroupId("");
    setSelectedBoardRelId("");
    setReadyCrewId("");
    setActiveDmUid("");
  };

  const handleSmartBack = React.useCallback(() => {
    if (onBack) return onBack();
    navigateTo(View.Map);
  }, [navigateTo, onBack]);

  const handleManageCrew = React.useCallback(() => {
    if (onManageCrew) return onManageCrew();
    navigateTo(View.CrewSettings);
  }, [navigateTo, onManageCrew]);

  const handleTogglePresence = React.useCallback(async () => {
    if (!uid || isPresenceSaving) return;
    const next = !presenceOptIn;
    const previous = presenceOptIn;
    const currentSettings = ((currentUser as any)?.notificationSettings || {}) as Record<string, any>;
    try {
      setIsPresenceSaving(true);
      setPresenceOptIn(next);
      await updateUserProfile(uid, {
        notificationSettings: {
          ...currentSettings,
          shareCrewMapPresence: next,
          crewMapPresence: next,
          crewMapPresenceEnabled: next,
        },
        updatedAt: Date.now(),
      } as any);
      if (!next) {
        await markOwnCrewPresenceOffline(uid).catch(() => undefined);
      } else {
        window.dispatchEvent(
          new CustomEvent("roddo:crew-presence-setting-changed", {
            detail: { uid, enabled: true, requestedAt: Date.now() },
          }),
        );
      }
      addToast(next ? "Crew map presence is on." : "Crew map presence is off.", "success");
    } catch (err) {
      console.error("[CrewView] crew presence toggle failed", err);
      setPresenceOptIn(previous);
      addToast("Could not update crew map presence.", "error");
    } finally {
      setIsPresenceSaving(false);
    }
  }, [uid, isPresenceSaving, presenceOptIn, currentUser, addToast]);

  const toggleNewCrewMember = (targetUid: string) => {
    const id = clean(targetUid);
    if (!id) return;
    setNewCrewMemberIds((prev) =>
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id],
    );
  };

  const handleCreateGroupCrew = async () => {
    const title = clean(newCrewName);
    if (!uid || !title || creatingCrew) return;
    try {
      setCreatingCrew(true);
      const crewId = await createGroupCrew({ owner: currentUser, title, memberIds: newCrewMemberIds });
      setNewCrewName("");
      setNewCrewMemberIds([]);
      setCreateCrewOpen(false);
      setSelectedBoardRelId("");
      setActiveGroupId(crewId);
      addToast(`${title} is ready.`, "success");
    } catch (err) {
      console.error("[CrewView] create group crew failed", err);
      addToast("Could not create that crew yet.", "error");
    } finally {
      setCreatingCrew(false);
    }
  };

  const openGroupManager = (crew: CrewChannel, screen: SettingsScreen = "overview") => {
    setActiveCrewMenuOpen(false);
    setManageGroupId(crew.id);
    setSettingsScreen(screen);
    setMemberMenuUid("");
    setEditCrewName(crew.title || "Fishing Crew");
    setEditCrewDescription(crew.description || "");
  };

  const chooseCrewCover = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      addToast("Choose an image file for the crew cover.", "error");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      addToast("Choose an image smaller than 20 MB.", "error");
      return;
    }
    if (coverPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(coverPreviewUrl);
    setCoverFile(file);
    setCoverPreviewUrl(URL.createObjectURL(file));
    setCoverRemoved(false);
  };

  const removeCrewCover = () => {
    if (coverPreviewUrl?.startsWith("blob:")) URL.revokeObjectURL(coverPreviewUrl);
    setCoverFile(null);
    setCoverPreviewUrl(null);
    setCoverRemoved(true);
  };

  const beginCrewCoverDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!coverPreviewUrl) return;
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    coverDragRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startPositionX: coverImagePositionX,
      startPositionY: coverImagePositionY,
      width: rect.width,
      height: rect.height,
    };
    setCoverDragging(true);
  };

  const moveCrewCoverDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = coverDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    event.preventDefault();
    const deltaX = ((event.clientX - drag.startClientX) / drag.width) * 100;
    const deltaY = ((event.clientY - drag.startClientY) / drag.height) * 100;
    setCoverImagePositionX(Math.min(100, Math.max(0, drag.startPositionX - deltaX)));
    setCoverImagePositionY(Math.min(100, Math.max(0, drag.startPositionY - deltaY)));
  };

  const endCrewCoverDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = coverDragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {}
    coverDragRef.current = null;
    setCoverDragging(false);
  };

  const saveGroupProfile = async () => {
    if (!managedGroup || managedGroup.ownerUid !== uid || groupManageBusy) return;
    const title = clean(editCrewName);
    if (!title) return;
    try {
      setGroupManageBusy(true);
      let coverPatch: Record<string, any> = {};
      if (coverFile) {
        coverPatch = await uploadCrewCover(managedGroup.id, coverFile);
      } else if (coverRemoved && (managedGroup.coverImageUrl || managedGroup.coverThumbUrl || managedGroup.coverImagePath)) {
        coverPatch = {
          coverImageUrl: null,
          coverThumbUrl: null,
          coverImagePath: null,
          coverImageUpdatedAt: Date.now(),
        };
      }
      await updateGroupCrewProfile({
        crewId: managedGroup.id,
        ownerUid: uid,
        title,
        description: editCrewDescription,
        ...coverPatch,
        coverImageZoom,
        coverImagePositionX,
        coverImagePositionY,
      });
      if (coverRemoved && (managedGroup.coverImageUrl || managedGroup.coverThumbUrl || managedGroup.coverImagePath)) {
        await deleteCrewCover(managedGroup.id);
      }
      addToast("Crew details updated.", "success");
      setSettingsScreen("overview");
    } catch (err) {
      console.error("[CrewView] update group crew failed", err);
      addToast("Could not update that crew.", "error");
    } finally {
      setGroupManageBusy(false);
    }
  };

  const addMemberToManagedGroup = async (memberUid: string) => {
    if (!managedGroup || managedGroup.ownerUid !== uid || groupManageBusy) return;
    try {
      setGroupManageBusy(true);
      await updateGroupCrewMembers(managedGroup.id, uid, [...managedGroup.memberIds, memberUid]);
      addToast("Angler added to the crew.", "success");
    } catch (err) {
      console.error("[CrewView] add group member failed", err);
      addToast("Could not add that angler.", "error");
    } finally {
      setGroupManageBusy(false);
    }
  };

  const removeMemberFromManagedGroup = async (memberUid: string, name: string) => {
    if (!managedGroup || managedGroup.ownerUid !== uid || memberUid === uid || groupManageBusy) return;
    setCrewConfirm({
      kind: "removeMember",
      crewId: managedGroup.id,
      memberUid,
      memberName: name || "this angler",
      crewTitle: managedGroup.title || "this crew",
    });
  };

  const toggleManagedGroupModerator = async (memberUid: string) => {
    if (!managedGroup || managedGroup.ownerUid !== uid || memberUid === uid || groupManageBusy) return;
    const currentRole = managedGroup.memberRoles?.[memberUid] === "moderator" ? "moderator" : "member";
    try {
      setGroupManageBusy(true);
      await setGroupCrewMemberRole({
        crewId: managedGroup.id,
        ownerUid: uid,
        memberUid,
        role: currentRole === "moderator" ? "member" : "moderator",
      });
      setMemberMenuUid("");
      addToast(currentRole === "moderator" ? "First Mate removed." : "First Mate promoted.", "success");
    } catch (err) {
      console.error("[CrewView] group role update failed", err);
      addToast("Could not update that role.", "error");
    } finally {
      setGroupManageBusy(false);
    }
  };

  const leaveManagedGroup = async () => {
    if (!managedGroup || managedGroup.ownerUid === uid || groupManageBusy) return;
    setCrewConfirm({
      kind: "leaveCrew",
      crewId: managedGroup.id,
      crewTitle: managedGroup.title || "this crew",
    });
  };

  const deleteManagedGroup = async () => {
    if (!managedGroup || managedGroup.ownerUid !== uid || groupManageBusy) return;
    setCrewConfirm({
      kind: "deleteCrew",
      crewId: managedGroup.id,
      crewTitle: managedGroup.title || "this crew",
    });
  };

  const confirmCrewAction = async () => {
    const target = crewConfirm;
    if (!target || groupManageBusy) return;

    try {
      setGroupManageBusy(true);

      if (target.kind === "removeMember") {
        const crew = groupCrews.find((item) => item.id === target.crewId);
        if (!crew) throw new Error("Crew not found.");

        await updateGroupCrewMembers(
          target.crewId,
          uid,
          crew.memberIds.filter((id) => id !== target.memberUid),
        );
        setMemberMenuUid("");
        setCrewConfirm(null);
        addToast(`${target.memberName} removed.`, "success");
        return;
      }

      if (target.kind === "leaveCrew") {
        await leaveGroupCrew(target.crewId, uid);
        if (activeGroupId === target.crewId) setActiveGroupId("");
        setManageGroupId("");
        setSettingsScreen("overview");
        setCrewConfirm(null);
        addToast("You left the crew.", "success");
        return;
      }

      await deleteGroupCrew(target.crewId, uid);
      if (activeGroupId === target.crewId) setActiveGroupId("");
      setManageGroupId("");
      setSettingsScreen("overview");
      setCrewConfirm(null);
      addToast(`${target.crewTitle} deleted.`, "success");
    } catch (err: any) {
      console.error("[CrewView] confirmed crew action failed", err);
      addToast(err?.message || "Could not complete that crew action.", "error");
    } finally {
      setGroupManageBusy(false);
    }
  };

  const handleCrewRequest = async (
    rel: UserRelationship,
    action: "accept" | "decline",
    targetUid: string,
    targetName: string,
  ) => {
    if (!uid || !rel?.id || !targetUid || requestBusyId) return;
    try {
      setRequestBusyId(rel.id);
      if (action === "accept") {
        await acceptFriendRequest(uid, targetUid);
        addToast(`${targetName} joined your crew.`, "success");
      } else {
        await declineFriendRequest(uid, targetUid);
        addToast("Crew request declined.", "info");
      }
    } catch (err) {
      console.error("[CrewView] crew request action failed", err);
      addToast("Could not update crew request.", "error");
    } finally {
      setRequestBusyId(null);
    }
  };

  const styles = React.useMemo(() => ({
    pageText: isDarkMode ? "#F2F6FC" : "#30271E",
    softText: isDarkMode ? "#A8B4C7" : "#6E6255",
    faintText: isDarkMode ? "#74839A" : "#8B7D6D",
    pageBg: isDarkMode ? "#070B15" : "#F7F2E8",
    surface: isDarkMode ? "#101827" : "#FFFCF7",
    surfaceRaised: isDarkMode ? "#0F1828" : "#F2EBE1",
    inputBg: isDarkMode ? "#0C1423" : "#FFFCF7",
    border: isDarkMode ? "rgba(132,151,190,0.14)" : "rgba(74,58,42,0.14)",
    borderStrong: isDarkMode ? "rgba(132,151,190,0.20)" : "rgba(74,58,42,0.22)",
    plum: isDarkMode ? "#8A70F2" : "#2F80ED",
    plumSoft: isDarkMode ? "rgba(138,112,242,0.10)" : "rgba(47,128,237,0.09)",
    green: isDarkMode ? "#5FD0A2" : "#197B58",
    danger: isDarkMode ? "#FF9AA5" : "#A63D49",
    shadow: isDarkMode ? "0 4px 12px rgba(0,0,0,0.12)" : "0 4px 14px rgba(62,45,28,0.06)",
    blue: isDarkMode ? "#8A70F2" : "#2F80ED",
    blueSoft: isDarkMode ? "rgba(138,112,242,0.10)" : "rgba(47,128,237,0.09)",
    mintSoft: isDarkMode ? "rgba(95,208,162,0.10)" : "rgba(25,123,88,0.08)",
    goldSoft: isDarkMode ? "rgba(242,184,75,0.08)" : "rgba(138,103,24,0.06)",
  }), [isDarkMode]);

  function renderSettings() {
    if (!managedGroup) return null;
    const isOwner = managedGroup.ownerUid === uid;
    const myRole = isOwner
      ? "Captain"
      : managedGroup.memberRoles?.[uid] === "moderator"
        ? "First Mate"
        : "Member";

    const members = (managedGroup.memberIds || []).map((memberUid) => {
      const memberUser = memberUid === uid ? currentUser : crewUserFor(memberUid);
      const role = memberUid === managedGroup.ownerUid
        ? "Captain"
        : managedGroup.memberRoles?.[memberUid] === "moderator"
          ? "First Mate"
          : "Member";
      return {
        uid: memberUid,
        user: memberUser,
        name: displayNameFor(memberUser, memberUid),
        avatar: avatarFor(memberUser),
        role,
      };
    });

    const addable = crewCards.filter((card) => !managedGroup.memberIds.includes(card.uid));

    const rowStyle: React.CSSProperties = {
      background: styles.surfaceRaised,
      border: `1px solid ${styles.border}`,
    };

    return (
      <div
        className="mx-auto flex w-full max-w-3xl lg:max-w-5xl flex-col gap-4 px-3 sm:px-4 lg:px-6 pb-28 pt-3 lg:pt-5"
        style={{ color: styles.pageText }}
      >
          <header className="flex items-center justify-between gap-3 border-b pb-3" style={{ borderColor: styles.border }}>
            <button
              type="button"
              onClick={() => {
                if (settingsScreen === "overview") setManageGroupId("");
                else setSettingsScreen("overview");
                setMemberMenuUid("");
              }}
              className="inline-flex items-center gap-1 text-sm font-black"
              style={{ color: styles.softText }}
            >
              <span className="text-xl">‹</span>
              {settingsScreen === "overview" ? managedGroup.title || "Crew" : "Crew Settings"}
            </button>
            <span className="text-xs font-black" style={{ color: styles.faintText }}>{myRole}</span>
          </header>

          {settingsScreen === "overview" ? (
            <div className="space-y-4">
              <section className="flex items-center gap-3">
                <div className="grid h-14 w-14 shrink-0 place-items-center rounded-[12px] text-lg font-black" style={{ background: styles.plumSoft, color: isDarkMode ? "#D8D1FF" : styles.plum }}>
                  {initialsFor(managedGroup.title || "Fishing Crew")}
                </div>
                <div className="min-w-0 flex-1">
                  <h1 className="truncate text-[22px] font-black">Crew Settings</h1>
                  <p className="mt-0.5 text-xs font-bold" style={{ color: styles.softText }}>
                    {managedGroup.memberIds.length} anglers · {myRole}
                  </p>
                </div>
              </section>

              <section className="space-y-3">
                <h2 className="text-[11px] font-black uppercase tracking-[0.14em]" style={{ color: styles.faintText }}>Crew cover</h2>
                <div className="overflow-hidden rounded-[16px]" style={rowStyle}>
                  <div
                    className={`relative aspect-[16/7] w-full overflow-hidden ${coverPreviewUrl ? (coverDragging ? "cursor-grabbing touch-none" : "cursor-grab touch-none") : ""}`}
                    onPointerDown={beginCrewCoverDrag}
                    onPointerMove={moveCrewCoverDrag}
                    onPointerUp={endCrewCoverDrag}
                    onPointerCancel={endCrewCoverDrag}
                    title={coverPreviewUrl ? "Drag image to reposition" : undefined}
                  >
                    {coverPreviewUrl ? (
                      <>
                        <img
                          src={coverPreviewUrl}
                          alt="Crew cover preview"
                          draggable={false}
                          onDragStart={(event) => event.preventDefault()}
                          className="pointer-events-none h-full w-full select-none object-cover transition-transform duration-200"
                          style={{
                            objectPosition: `${coverImagePositionX}% ${coverImagePositionY}%`,
                            transform: `scale(${coverImageZoom})`,
                            transformOrigin: `${coverImagePositionX}% ${coverImagePositionY}%`,
                          }}
                        />
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/12 to-transparent" />
                        <div className="pointer-events-none absolute right-3 top-3 rounded-full border border-white/15 bg-black/35 px-2.5 py-1 text-[10px] font-black text-white/85 backdrop-blur-md">
                          Drag to reposition
                        </div>
                        <div className="pointer-events-none absolute bottom-3 left-3 right-3 text-white">
                          <div className="text-[10px] font-black uppercase tracking-[.12em] text-white/70">Private Crew</div>
                          <div className="mt-0.5 truncate text-base font-black">{editCrewName || managedGroup.title || "Fishing Crew"}</div>
                          <div className="mt-0.5 truncate text-[11px] font-semibold text-white/75">{managedGroup.memberIds.length} anglers · {myRole}</div>
                        </div>
                      </>
                    ) : (
                      <div className="grid h-full place-items-center px-6 text-center" style={{ background: styles.plumSoft }}>
                        <div>
                          <div className="text-2xl">🎣</div>
                          <div className="mt-1 text-sm font-black">Add a crew cover</div>
                          <div className="mt-1 text-[11px] font-semibold" style={{ color: styles.softText }}>Water, a boat, a catch, or crew artwork all work well.</div>
                        </div>
                      </div>
                    )}
                  </div>

                  {isOwner ? (
                    <>
                      <div className="flex flex-wrap items-center justify-between gap-2 border-t px-3 py-2.5" style={{ borderColor: styles.border }}>
                        <div className="text-[10px] font-semibold" style={{ color: styles.faintText }}>Compressed automatically for Dock + crew hero.</div>
                        <div className="flex gap-2">
                          {coverPreviewUrl ? (
                            <button type="button" onClick={removeCrewCover} className="rounded-lg px-2.5 py-1.5 text-[11px] font-black" style={{ color: styles.danger }}>Remove</button>
                          ) : null}
                          <label className="cursor-pointer rounded-lg border px-2.5 py-1.5 text-[11px] font-black" style={{ background: styles.plumSoft, borderColor: styles.borderStrong, color: styles.plum }}>
                            {coverPreviewUrl ? "Replace image" : "Choose image"}
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                              className="hidden"
                              onChange={(event) => {
                                chooseCrewCover(event.target.files?.[0] || null);
                                event.currentTarget.value = "";
                              }}
                            />
                          </label>
                        </div>
                      </div>

                      {coverPreviewUrl ? (
                        <div className="border-t px-3 py-3" style={{ borderColor: styles.border }}>
                          <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                            <label className="block">
                              <div className="mb-1.5 flex items-center justify-between gap-3 text-[11px] font-black">
                                <span>Image zoom</span>
                                <span style={{ color: styles.faintText }}>{Math.round(coverImageZoom * 100)}%</span>
                              </div>
                              <input
                                type="range"
                                min="1"
                                max="1.6"
                                step="0.05"
                                value={coverImageZoom}
                                onChange={(event) => setCoverImageZoom(Number(event.target.value))}
                                className="w-full accent-[#2F80ED] dark:accent-[#8A70F2]"
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                setCoverImageZoom(1);
                                setCoverImagePositionX(50);
                                setCoverImagePositionY(50);
                              }}
                              className="rounded-lg px-2.5 py-1.5 text-[11px] font-black"
                              style={{ color: styles.softText }}
                            >
                              Reset
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </section>

              <section className="space-y-3">
                <h2 className="text-[11px] font-black uppercase tracking-[0.14em]" style={{ color: styles.faintText }}>Crew details</h2>
                <div className="rounded-[12px] p-3.5" style={rowStyle}>
                  <label className="text-xs font-black" style={{ color: styles.softText }}>Name</label>
                  <input
                    value={editCrewName}
                    onChange={(e) => setEditCrewName(e.target.value)}
                    disabled={!isOwner}
                    maxLength={48}
                    className="mt-2 w-full bg-transparent text-lg font-black outline-none disabled:opacity-70"
                  />
                  <div className="my-4 h-px" style={{ background: styles.border }} />
                  <label className="text-xs font-black" style={{ color: styles.softText }}>Description</label>
                  <textarea
                    value={editCrewDescription}
                    onChange={(e) => setEditCrewDescription(e.target.value)}
                    disabled={!isOwner}
                    rows={2}
                    maxLength={180}
                    placeholder="What is this crew about?"
                    className="mt-2 w-full resize-none bg-transparent text-sm font-semibold leading-5 outline-none disabled:opacity-70"
                  />
                  {isOwner ? (
                    <button
                      type="button"
                      onClick={() => void saveGroupProfile()}
                      disabled={groupManageBusy || !clean(editCrewName)}
                      className="mt-3 w-full rounded-[10px] px-4 py-3 text-sm font-black disabled:opacity-50"
                      style={{
              background: isDarkMode ? styles.plum : styles.blue,
              color: "white",
              boxShadow: isDarkMode
                ? "0 10px 22px rgba(92,67,197,.22)"
                : "0 10px 22px rgba(47,128,237,.20)",
            }}
                    >
                      {groupManageBusy ? "Saving..." : "Save changes"}
                    </button>
                  ) : null}
                </div>
              </section>

              <section className="space-y-3">
                <h2 className="text-[11px] font-black uppercase tracking-[0.14em]" style={{ color: styles.faintText }}>People</h2>
                <button
                  type="button"
                  onClick={() => setSettingsScreen("members")}
                  className="flex w-full items-center gap-3 rounded-[12px] p-4 text-left"
                  style={rowStyle}
                >
                  <div className="grid h-11 w-11 place-items-center rounded-full" style={{ background: styles.plumSoft }}>👥</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-black">Members</div>
                    <div className="mt-0.5 text-xs font-bold" style={{ color: styles.softText }}>{managedGroup.memberIds.length} anglers · roles and removal</div>
                  </div>
                  <span className="text-xl" style={{ color: styles.faintText }}>›</span>
                </button>

                {isOwner ? (
                  <button
                    type="button"
                    onClick={() => setSettingsScreen("add")}
                    className="flex w-full items-center gap-3 rounded-[12px] p-4 text-left"
                    style={rowStyle}
                  >
                    <div className="grid h-11 w-11 place-items-center rounded-full" style={{ background: styles.plumSoft }}>＋</div>
                    <div className="min-w-0 flex-1">
                      <div className="font-black">Add anglers</div>
                      <div className="mt-0.5 text-xs font-bold" style={{ color: styles.softText }}>Invite from your accepted crewmates</div>
                    </div>
                    <span className="text-xl" style={{ color: styles.faintText }}>›</span>
                  </button>
                ) : null}
              </section>

              <section className="pt-2">
                <div className="h-px" style={{ background: styles.border }} />
                <div className="pt-5">
                  <h2 className="text-[11px] font-black uppercase tracking-[0.14em]" style={{ color: styles.faintText }}>Danger zone</h2>
                  {isOwner ? (
                    <button
                      type="button"
                      onClick={() => void deleteManagedGroup()}
                      disabled={groupManageBusy}
                      className="mt-3 w-full rounded-[12px] border px-4 py-3 text-sm font-black disabled:opacity-50"
                      style={{ borderColor: "rgba(239,106,115,0.28)", color: styles.danger }}
                    >
                      Delete crew
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => void leaveManagedGroup()}
                      disabled={groupManageBusy}
                      className="mt-3 w-full rounded-[12px] border px-4 py-3 text-sm font-black disabled:opacity-50"
                      style={{ borderColor: "rgba(239,106,115,0.28)", color: styles.danger }}
                    >
                      Leave crew
                    </button>
                  )}
                </div>
              </section>
            </div>
          ) : settingsScreen === "members" ? (
            <div className="space-y-3">
              <div className="mb-5">
                <h1 className="text-[26px] font-black">Members</h1>
                <p className="mt-1 text-sm font-bold" style={{ color: styles.softText }}>Your crew roster and roles.</p>
              </div>

              {members.map((member) => {
                const canManage = isOwner && member.uid !== uid;
                const isMenuOpen = memberMenuUid === member.uid;
                return (
                  <div key={member.uid} className="relative flex items-center gap-3 rounded-[12px] p-3" style={rowStyle}>
                    <button type="button" onClick={() => openProfile(member.uid)} className="h-12 w-12 overflow-hidden rounded-full">
                      <img src={member.avatar} alt="" className="h-full w-full object-cover" />
                    </button>
                    <div className="min-w-0 flex-1">
                      <button type="button" onClick={() => openProfile(member.uid)} className="truncate text-left text-sm font-black">{member.name}</button>
                      <div className="mt-0.5 text-xs font-bold" style={{ color: member.role === "Captain" ? "#FFD66B" : styles.softText }}>{member.role}</div>
                    </div>
                    {canManage ? (
                      <button
                        type="button"
                        onClick={() => setMemberMenuUid((prev) => prev === member.uid ? "" : member.uid)}
                        className="grid h-10 w-10 place-items-center rounded-full text-xl font-black"
                        style={{ background: styles.plumSoft }}
                      >
                        ⋯
                      </button>
                    ) : null}

                    {isMenuOpen ? (
                      <div className="absolute right-3 top-14 z-20 w-52 overflow-hidden rounded-[10px] border p-1 shadow-2xl" style={{ background: styles.surface, borderColor: styles.borderStrong }}>
                        <button
                          type="button"
                          onClick={() => void toggleManagedGroupModerator(member.uid)}
                          className="w-full rounded-xl px-3 py-2.5 text-left text-sm font-black"
                        >
                          {member.role === "First Mate" ? "Remove First Mate" : "Make First Mate"}
                        </button>
                        <button
                          type="button"
                          onClick={() => void removeMemberFromManagedGroup(member.uid, member.name)}
                          className="w-full rounded-xl px-3 py-2.5 text-left text-sm font-black"
                          style={{ color: styles.danger }}
                        >
                          Remove from crew
                        </button>
                      </div>
                    ) : null}
                  </div>
                );
              })}

              {isOwner ? (
                <button
                  type="button"
                  onClick={() => setSettingsScreen("add")}
                  className="mt-2 w-full rounded-[12px] border border-dashed px-4 py-4 text-sm font-black"
                  style={{ borderColor: styles.borderStrong, color: styles.plum }}
                >
                  + Add angler
                </button>
              ) : null}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="mb-5">
                <h1 className="text-[26px] font-black">Add Anglers</h1>
                <p className="mt-1 text-sm font-bold" style={{ color: styles.softText }}>Choose from accepted crewmates.</p>
              </div>
              {addable.length ? addable.map((card) => (
                <div key={card.uid} className="flex items-center gap-3 rounded-[12px] p-3" style={rowStyle}>
                  <CrewAvatar
                    user={card.user}
                    name={card.name}
                    sizeClass="h-12 w-12"
                    ringColor={styles.borderStrong}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-black">{card.name}</div>
                    <div className="mt-0.5 text-xs font-bold" style={{ color: styles.softText }}>{card.isOnline ? "Online" : "Crewmate"}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void addMemberToManagedGroup(card.uid)}
                    disabled={groupManageBusy}
                    className="rounded-full px-4 py-2 text-xs font-black disabled:opacity-50"
                    style={{ background: styles.plum, color: "white" }}
                  >
                    Add
                  </button>
                </div>
              )) : (
                <div className="rounded-[12px] p-5 text-center text-sm font-bold" style={rowStyle}>
                  <div>No more accepted crewmates to add.</div>
                  <button type="button" onClick={handleManageCrew} className="mt-3 font-black" style={{ color: styles.plum }}>Invite anglers</button>
                </div>
              )}
            </div>
          )}

        <RoddoConfirmDialog
          open={!!crewConfirm}
          title={
            crewConfirm?.kind === "deleteCrew"
              ? `Delete ${crewConfirm.crewTitle}?`
              : crewConfirm?.kind === "leaveCrew"
                ? `Leave ${crewConfirm.crewTitle}?`
                : `Remove ${crewConfirm?.memberName || "this angler"}?`
          }
          description={
            crewConfirm?.kind === "deleteCrew"
              ? "This removes the crew from every member's Crew Dock."
              : crewConfirm?.kind === "leaveCrew"
                ? "You will lose access to this private crew and its board."
                : `This removes ${crewConfirm?.memberName || "this angler"} from ${crewConfirm && "crewTitle" in crewConfirm ? crewConfirm.crewTitle : "this crew"}.`
          }
          confirmLabel={
            crewConfirm?.kind === "deleteCrew"
              ? "Delete crew"
              : crewConfirm?.kind === "leaveCrew"
                ? "Leave crew"
                : "Remove"
          }
          cancelLabel="Cancel"
          tone="danger"
          isBusy={groupManageBusy}
          isDarkMode={isDarkMode}
          onConfirm={confirmCrewAction}
          onCancel={() => {
            if (!groupManageBusy) setCrewConfirm(null);
          }}
        />
      </div>
    );
  }

  if (managedGroup) {
    return renderSettings();
  }

  if (activeGroup) {
    const role = activeGroup.ownerUid === uid
      ? "Captain"
      : activeGroup.memberRoles?.[uid] === "moderator"
        ? "First Mate"
        : "Member";
    const canModerate = role === "Captain" || role === "First Mate";
    const groupMembers = (activeGroup.memberIds || []).map((memberUid) => {
      const memberUser = memberUid === uid ? currentUser : crewUserFor(memberUid);
      return {
        uid: memberUid,
        user: memberUser,
        name: displayNameFor(memberUser, memberUid),
        avatar: avatarFor(memberUser),
        online: onlineCrewUids.has(memberUid),
      };
    });

    return (
      <CrewPageShell color={styles.pageText} isDarkMode={isDarkMode}>
        <header className="pb-1 pt-1">
          <div className="grid grid-cols-[88px_1fr_132px] items-center gap-2">
            <div className="flex w-[88px] justify-start">
              <div
                className="grid h-12 w-12 place-items-center rounded-[10px] text-sm font-black"
                style={{
                  background: styles.plumSoft,
                  color: isDarkMode ? "#D8D1FF" : styles.plum,
                  border: `1px solid ${styles.border}`,
                }}
                aria-label={`${activeGroup.title || "Fishing Crew"} symbol`}
              >
                {initialsFor(activeGroup.title || "Fishing Crew")}
              </div>
            </div>

            <div className="min-w-0 text-center">
              <h1 className="truncate text-[26px] font-black leading-tight tracking-tight">
                {activeGroup.title || "Fishing Crew"}
              </h1>
              <p className="mt-1 text-xs font-bold" style={{ color: styles.softText }}>
                {activeGroup.memberIds.length} anglers · {role}
              </p>
            </div>

            <div className="flex w-[132px] items-center justify-end gap-2">
              <button
                type="button"
                onClick={backFromCrew}
                className="grid h-10 w-10 place-items-center rounded-full text-2xl font-black"
                style={{ background: styles.surfaceRaised, color: styles.pageText }}
                aria-label="Back to Crew Dock"
                title="Crew Dock"
              >
                ‹
              </button>

              <div ref={activeCrewMenuRef} className="relative">
                <button
                  type="button"
                  onClick={() => setActiveCrewMenuOpen((open) => !open)}
                  className="grid h-10 w-10 place-items-center rounded-full text-xl font-black leading-none"
                  style={{ background: styles.surfaceRaised, color: styles.pageText }}
                  aria-label="Crew menu"
                  aria-expanded={activeCrewMenuOpen}
                  title="Crew menu"
                >
                  ⋯
                </button>

                {activeCrewMenuOpen ? (
                  <div
                    className="absolute right-0 top-12 z-50 w-52 overflow-hidden rounded-[12px] border p-1 shadow-2xl"
                    style={{ background: styles.surface, borderColor: styles.borderStrong }}
                    role="menu"
                  >
                    <button
                      type="button"
                      onClick={() => openGroupManager(activeGroup)}
                      className="w-full rounded-[9px] px-3 py-2.5 text-left text-sm font-black"
                      style={{ color: styles.pageText }}
                      role="menuitem"
                    >
                      ✏️ Edit crew
                    </button>
                    <button
                      type="button"
                      onClick={() => openGroupManager(activeGroup, "members")}
                      className="w-full rounded-[9px] px-3 py-2.5 text-left text-sm font-black"
                      style={{ color: styles.pageText }}
                      role="menuitem"
                    >
                      👥 Manage members
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        setActiveCrewMenuOpen(false);
                        const result = await share({
                          url: buildCrewShareUrl(activeGroup.id),
                        });
                        if (result.ok && result.method === "clipboard") {
                          addToast("Crew link copied.", "success");
                        } else if (!result.ok) {
                          addToast("Could not share that crew.", "error");
                        }
                      }}
                      className="w-full rounded-[9px] px-3 py-2.5 text-left text-sm font-black"
                      style={{ color: styles.pageText }}
                      role="menuitem"
                    >
                      <span className="inline-flex items-center gap-2">
                        <ShareIcon className="h-[16px] w-[16px]" />
                        <span>Share crew</span>
                      </span>
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </header>

        <div className="h-px" style={{ background: styles.border }} />

        <section className="pt-5">
          <div className="overflow-hidden rounded-[20px] border" style={{ background: styles.surface, borderColor: styles.border, boxShadow: styles.shadow }}>
            <div className="relative h-[190px] overflow-hidden">
              {crewCoverUrl(activeGroup) ? (
                <img src={crewCoverUrl(activeGroup)} alt="" className="absolute inset-0 h-full w-full object-cover" style={crewCoverStyle(activeGroup)} />
              ) : (
                <div
                        className="absolute inset-0"
                        style={{
                          background: isDarkMode
                            ? "radial-gradient(circle at 20% 15%, rgba(138,112,242,.16), transparent 42%), linear-gradient(145deg,#101827,#0B1321)"
                            : "radial-gradient(circle at 20% 15%, rgba(47,128,237,.15), transparent 42%), linear-gradient(145deg,#EDF5FF,#E8EEF6)",
                        }}
                      />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/72 via-black/22 to-transparent" />
              <div className="absolute left-4 top-4 rounded-full border border-white/15 bg-black/35 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white">Private Crew</div>
              <div className="absolute bottom-4 left-4 right-4 text-white">
                <div className="flex items-end justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-[26px] font-black leading-tight">{activeGroup.title || "Fishing Crew"}</h2>
                    <p className="mt-1 line-clamp-2 text-sm font-semibold text-white/80">{activeGroup.description || "Private crew chatter, trip planning, and trusted updates."}</p>
                  </div>
                  <button type="button" onClick={() => openGroupManager(activeGroup, "members")} className="shrink-0 rounded-full border border-white/20 bg-black/25 px-3 py-2 text-[11px] font-black text-white backdrop-blur-sm">
                    {groupMembers.length} {groupMembers.length === 1 ? "Member" : "Members"}
                  </button>
                </div>
                <div className="mt-3 flex -space-x-2">
                  {groupMembers.slice(0, 6).map((member) => (
                    <button key={member.uid} type="button" onClick={() => openProfile(member.uid)} className="relative h-10 w-10 rounded-full" title={member.name}>
                      <CrewAvatar
                        user={member.user}
                        name={member.name}
                        sizeClass="h-10 w-10"
                        ringColor="#FFFFFF"
                      />
                      <span className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-[#0A1120]" style={{ background: member.online ? styles.green : "#73717f" }} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="pt-3">
        <CrewBoardView
          crewId={activeGroup.id}
          crewTitle={activeGroup.title || "Fishing Crew"}
          memberNames={groupMembers.map((member) => member.name)}
          currentUser={currentUser}
          addToast={addToast}
          isDarkMode={isDarkMode}
          onOpenProfile={openProfile}
          canModerate={canModerate}
          initialPostId={initialCrewPostId}
        />
        </div>

      </CrewPageShell>
    );
  }

  if (activeDmCard) {
    const dmRelationship = accepted.find((rel) => otherUid(rel, uid) === activeDmCard.uid) || null;
    const dmId = dmRelationship?.id || dmInboxByUid.get(activeDmCard.uid)?.id || '';

    return (
      <CrewPageShell color={styles.pageText} isDarkMode={isDarkMode}>
        <CrewDMView
          dmId={dmId}
          targetUid={activeDmCard.uid}
          targetName={activeDmCard.name}
          targetAvatar={activeDmCard.avatar}
          targetOnline={activeDmCard.isOnline}
          currentUser={currentUser}
          isDarkMode={isDarkMode}
          addToast={addToast}
          onBack={() => setActiveDmUid('')}
          onOpenProfile={() => openProfile(activeDmCard.uid)}
          onOpenBoard={
            dmRelationship
              ? () => {
                  setActiveDmUid('');
                  setActiveGroupId('');
                  setSelectedBoardRelId(dmRelationship.id);
                }
              : undefined
          }
        />
      </CrewPageShell>
    );
  }

  if (selectedBoardRelationship && readyCrewId) {
    return (
      <CrewPageShell color={styles.pageText} isDarkMode={isDarkMode}>
        <header className="flex items-center justify-between gap-3 py-1">
          <button
            type="button"
            onClick={backFromCrew}
            className="inline-flex items-center gap-1 text-sm font-black"
            style={{ color: styles.softText }}
          >
            <span className="text-xl">‹</span> Crew Dock
          </button>
          <button
            type="button"
            onClick={() => openProfile(selectedBoardUid)}
            className="text-xs font-black"
            style={{ color: styles.plum }}
          >
            View Profile
          </button>
        </header>

        <section className="flex items-center gap-4 py-2">
          <img src={avatarFor(selectedBoardUser)} alt="" className="h-16 w-16 rounded-full object-cover" />
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.14em]" style={{ color: styles.faintText }}>
              Private board
            </div>
            <h1 className="mt-1 text-[26px] font-black leading-tight">{selectedBoardName}</h1>
            <p className="mt-1 text-sm font-bold" style={{ color: styles.softText }}>
              One-to-one crew chatter
            </p>
          </div>
        </section>

        <div className="h-px" style={{ background: styles.border }} />

        <CrewBoardView
          crewId={readyCrewId}
          crewTitle={`${selectedBoardName} Crew`}
          memberNames={[displayNameFor(currentUser, uid), selectedBoardName].filter(Boolean)}
          currentUser={currentUser}
          addToast={addToast}
          isDarkMode={isDarkMode}
          onOpenProfile={openProfile}
          initialPostId={initialCrewPostId}
        />
      </CrewPageShell>
    );
  }



  return (
    <CrewPageShell color={styles.pageText} isDarkMode={isDarkMode}>
      <header className="pb-2 pt-1">
        <div className="grid grid-cols-[48px_1fr_48px] items-center gap-3">
          <button
            type="button"
            onClick={() => void handleTogglePresence()}
            disabled={isPresenceSaving || !uid}
            className="relative grid h-12 w-12 place-items-center rounded-[10px] border transition-transform hover:-translate-y-0.5 active:scale-[0.98] disabled:opacity-60"
            style={{
              background: styles.surfaceRaised,
              borderColor: presenceOptIn
                ? isDarkMode
                  ? "rgba(152,226,255,0.20)"
                  : "rgba(124,199,255,0.24)"
                : isDarkMode
                  ? "rgba(255,255,255,0.10)"
                  : "rgba(91,67,49,0.10)",
              boxShadow: isDarkMode
                ? "0 10px 18px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.04)"
                : "0 8px 16px rgba(89,65,37,0.08), inset 0 1px 0 rgba(255,255,255,0.72)",
            }}
            aria-label={presenceOptIn ? "Hide crew map presence" : "Show crew map presence"}
            title={presenceOptIn ? "Visible to crew" : "Hidden from crew"}
          >
            <span
              className="text-[24px] leading-none"
              aria-hidden="true"
              style={{
                filter: presenceOptIn ? "none" : "grayscale(0.12)",
                opacity: presenceOptIn ? 1 : 0.82,
              }}
            >
              {presenceOptIn ? "🐵" : "🙈"}
            </span>

            <span
              className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2"
              aria-hidden="true"
              style={{
                background: presenceOptIn ? "#34d399" : "#73717f",
                borderColor: styles.pageBg,
                boxShadow: "none",
              }}
            />
          </button>

          <div className="min-w-0 text-center">
            <div
              className="mx-auto mb-1.5 w-fit rounded-full border px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em]"
              style={{
                background: styles.blueSoft,
                borderColor: styles.border,
                color: isDarkMode ? "#D9D2FF" : styles.blue,
              }}
            >
              Private crews
            </div>
            <h1 className="text-[28px] font-black leading-tight tracking-tight">
              Crew Dock
            </h1>
            <p
              className="mt-1 text-[13px] font-semibold"
              style={{ color: styles.softText }}
            >
              Your trusted anglers and private crews
            </p>
          </div>

          <button
            type="button"
            onClick={handleSmartBack}
            className="grid h-10 w-10 place-items-center justify-self-end rounded-full text-2xl font-black"
            style={{ background: styles.surfaceRaised }}
            aria-label="Back"
          >
            ‹
          </button>
        </div>
      </header>

      <section
        className="crew-card rounded-[18px] border p-4"
        style={{
          background: styles.surface,
          borderColor: styles.border,
          boxShadow: styles.shadow,
        }}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-black">Crewmates</h2>
            <p className="mt-0.5 text-xs font-bold" style={{ color: styles.softText }}>
              Tap someone to message. Private boards stay inside chat.
            </p>
          </div>

          <button
            type="button"
            onClick={handleManageCrew}
            className="text-xs font-black"
            style={{ color: isDarkMode ? styles.plum : styles.blue }}
          >
            Invite Angler
          </button>
        </div>

        {crewCards.length ? (
          <div className="-mx-1 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex min-w-max gap-2.5">
              {crewCards.map((card) => (
                <button
                  key={card.rel.id}
                  type="button"
                  onClick={() => {
                    setActiveGroupId("");
                    setSelectedBoardRelId("");
                    setReadyCrewId("");
                    setActiveDmUid(card.uid);
                  }}
                  className="w-[78px] shrink-0 px-1 py-1 text-center"
                  style={{
                    background: "transparent",
                    border: "none",
                  }}
                >
                  <div className="relative mx-auto h-14 w-14">
                    <CrewAvatar
                      user={card.user}
                      name={card.name}
                      sizeClass="h-14 w-14"
                      ringColor={styles.borderStrong}
                    />
                    <span
                      className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2"
                      style={{
                        background: card.isOnline
                          ? styles.green
                          : isDarkMode
                            ? "#625C72"
                            : "#A8A29E",
                        borderColor: styles.surface,
                      }}
                    />
                    {dmInboxByUid.get(card.uid)?.unread ? (
                      <span
                        className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full px-1 text-[8px] font-black text-white"
                        style={{ background: styles.plum }}
                        aria-label={`Unread message from ${card.name}`}
                      >
                        •
                      </span>
                    ) : null}
                  </div>

                  <div className="mt-2 truncate text-[11px] font-black leading-tight">
                    {card.name}
                  </div>
                  <div
                    className="mt-1 text-[9px] font-black uppercase tracking-[0.08em]"
                    style={{
                      color: card.isOnline ? styles.green : styles.faintText,
                    }}
                  >
                    {card.isOnline ? "Online" : "Offline"}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div
            className="rounded-[12px] p-4 text-sm font-semibold"
            style={{ background: styles.surface, color: styles.softText }}
          >
            No accepted crewmates yet.
          </div>
        )}
      </section>

      {dmInbox.some((row) => Number(row.lastMessageAt || 0) > 0) ? (
        <section
          className="crew-card rounded-[18px] border p-4"
          style={{
            background: styles.surface,
            borderColor: styles.border,
            boxShadow: styles.shadow,
          }}
        >
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-black">Messages</h2>
              <p className="mt-0.5 text-xs font-bold" style={{ color: styles.softText }}>
                Private one-to-one Crew chat
              </p>
            </div>
            {dmInbox.filter((row) => row.unread).length ? (
              <span
                className="rounded-full px-2.5 py-1 text-[10px] font-black"
                style={{ background: styles.plumSoft, color: isDarkMode ? '#D9D2FF' : styles.plum }}
              >
                {dmInbox.filter((row) => row.unread).length} unread
              </span>
            ) : null}
          </div>

          <div className="max-h-[360px] space-y-2 overflow-y-auto pr-1 roddo-content-scroll">
            {dmInbox
              .filter((row) => Number(row.lastMessageAt || 0) > 0)
              .map((row) => {
                const card = crewCards.find((item) => item.uid === row.otherUid);
                if (!card) return null;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onClick={() => {
                      setActiveGroupId('');
                      setSelectedBoardRelId('');
                      setReadyCrewId('');
                      setActiveDmUid(card.uid);
                    }}
                    className="flex w-full items-center gap-3 rounded-[13px] border p-3 text-left transition hover:-translate-y-0.5"
                    style={{
                      background: row.unread ? styles.plumSoft : styles.surfaceRaised,
                      borderColor: row.unread ? styles.borderStrong : styles.border,
                    }}
                  >
                    <div className="relative h-11 w-11 shrink-0">
                      <CrewAvatar
                        user={card.user}
                        name={card.name}
                        sizeClass="h-11 w-11"
                        ringColor={styles.borderStrong}
                      />
                      <span
                        className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2"
                        style={{
                          background: card.isOnline ? styles.green : isDarkMode ? '#625C72' : '#A8A29E',
                          borderColor: row.unread ? styles.plumSoft : styles.surfaceRaised,
                        }}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <span className="truncate text-[12px] font-black">{card.name}</span>
                        {row.unread ? (
                          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: styles.plum }} />
                        ) : null}
                      </div>
                      <div className="mt-1 truncate text-[11px] font-semibold" style={{ color: styles.softText }}>
                        {row.lastMessageSenderId === uid ? 'You: ' : ''}{row.lastMessageText || 'Crew message'}
                      </div>
                    </div>
                  </button>
                );
              })}
          </div>
        </section>
      ) : null}

      <div className="h-px" style={{ background: styles.border }} />

      <section
        className="crew-card rounded-[18px] border p-4"
        style={{
          background: styles.surface,
          borderColor: styles.border,
          boxShadow: styles.shadow,
        }}
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-black">My Crews</h2>
            <p className="mt-0.5 text-xs font-bold" style={{ color: styles.softText }}>
              Private groups for your different fishing circles.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setCreateCrewOpen((open) => !open)}
            className="rounded-full px-4 py-2 text-xs font-black"
            style={{ background: styles.plum, color: "white" }}
          >
            {createCrewOpen ? "Close" : "+ Create"}
          </button>
        </div>

        {createCrewOpen ? (
          <div
            className="mb-3 rounded-[12px] p-4"
            style={{
              background: styles.surface,
              border: `1px solid ${styles.border}`,
            }}
          >
            <input
              value={newCrewName}
              onChange={(e) => setNewCrewName(e.target.value)}
              maxLength={48}
              placeholder="Crew name"
              className="w-full bg-transparent text-lg font-black outline-none"
            />

            <div className="mt-3 h-px" style={{ background: styles.border }} />

            <div
              className="mt-3 text-xs font-black"
              style={{ color: styles.softText }}
            >
              Add crewmates now (optional)
            </div>

            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {crewCards.map((card) => {
                const selected = newCrewMemberIds.includes(card.uid);

                return (
                  <button
                    key={card.uid}
                    type="button"
                    onClick={() => toggleNewCrewMember(card.uid)}
                    className="w-[72px] shrink-0 text-center"
                  >
                    <div className="relative mx-auto h-11 w-11">
                      <div
                        className="rounded-full"
                        style={{
                          outline: selected ? `3px solid ${styles.plum}` : "none",
                          outlineOffset: selected ? "2px" : undefined,
                        }}
                      >
                        <CrewAvatar
                          user={card.user}
                          name={card.name}
                          sizeClass="h-11 w-11"
                          ringColor={styles.borderStrong}
                        />
                      </div>
                      {selected ? (
                        <span
                          className="absolute -right-1 -top-1 grid h-5 w-5 place-items-center rounded-full text-[10px] font-black text-white"
                          style={{ background: styles.plum }}
                        >
                          ✓
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-1 truncate text-[10px] font-black">
                      {card.name.split(/\s+/)[0]}
                    </div>
                  </button>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => void handleCreateGroupCrew()}
              disabled={creatingCrew || !clean(newCrewName)}
              className="mt-4 w-full rounded-[10px] px-4 py-3 text-sm font-black disabled:opacity-50"
              style={{ background: styles.plum, color: "white" }}
            >
              {creatingCrew ? "Creating..." : "Create Crew"}
            </button>
          </div>
        ) : null}

        <div className="space-y-2">
          {groupCrews.length ? (
            groupCrews.map((crew) => {
              const memberCount = crew.memberIds?.length || 0;
              const role =
                crew.ownerUid === uid
                  ? "Captain"
                  : crew.memberRoles?.[uid] === "moderator"
                    ? "First Mate"
                    : "Member";

              return (
                <button
                  key={crew.id}
                  type="button"
                  onClick={() => {
                    setSelectedBoardRelId("");
                    setActiveGroupId(crew.id);
                  }}
                  className="w-full overflow-hidden rounded-[16px] text-left transition hover:-translate-y-0.5"
                  style={{ background: styles.surface, border: `1px solid ${styles.border}` }}
                >
                  <div className="relative h-28 overflow-hidden">
                    {crewCoverUrl(crew, true) ? (
                      <img src={crewCoverUrl(crew, true)} alt="" className="absolute inset-0 h-full w-full object-cover" style={crewCoverStyle(crew)} />
                    ) : (
                      <div className="absolute inset-0" style={{ background: isDarkMode ? "#0F1828" : "#EAF2FF" }} />
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent" />
                    <div className="absolute left-3 top-3 rounded-full border border-white/15 bg-black/30 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.12em] text-white">Private Crew</div>
                    <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-base font-black text-white">{crew.title || "Fishing Crew"}</div>
                        <div className="mt-0.5 text-xs font-bold text-white/80">{memberCount} {memberCount === 1 ? "angler" : "anglers"} · {role}</div>
                      </div>
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-black/25 text-xl text-white">›</span>
                    </div>
                  </div>
                </button>
              );
            })
          ) : (
            <button
              type="button"
              onClick={() => setCreateCrewOpen(true)}
              className="w-full rounded-[10px] border border-dashed p-4 text-left"
              style={{
                borderColor: isDarkMode ? styles.borderStrong : "rgba(47,128,237,.22)",
                background: styles.surfaceRaised,
              }}
            >
              <div className="font-black">Create your first crew</div>
              <div
                className="mt-1 text-sm font-semibold"
                style={{ color: styles.softText }}
              >
                Make a private space for a fishing circle.
              </div>
            </button>
          )}
        </div>
      </section>

      {incoming.length ? (
        <>
          <div className="h-px" style={{ background: styles.border }} />

          <section>
            <h2 className="text-lg font-black">Requests</h2>

            <div className="mt-3 space-y-2">
              {incoming.map((rel) => {
                const targetUid = otherUid(rel, uid);
                const targetUser = users[targetUid] as any;
                const name = displayNameFor(targetUser, targetUid);
                const busy = requestBusyId === rel.id;

                return (
                  <div
                    key={rel.id}
                    className="flex items-center gap-3 rounded-[12px] p-3"
                    style={{ background: styles.surface }}
                  >
                    <CrewAvatar
                      user={targetUser}
                      name={name}
                      sizeClass="h-12 w-12"
                      ringColor={styles.borderStrong}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-black">{name}</div>
                      <div
                        className="mt-0.5 text-xs font-bold"
                        style={{ color: styles.softText }}
                      >
                        wants to join your crew
                      </div>
                    </div>

                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void handleCrewRequest(rel, "accept", targetUid, name)
                      }
                      className="rounded-full px-3 py-2 text-xs font-black disabled:opacity-50"
                      style={{ background: styles.plum, color: "white" }}
                    >
                      Accept
                    </button>

                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void handleCrewRequest(rel, "decline", targetUid, name)
                      }
                      className="px-2 py-2 text-xs font-black disabled:opacity-50"
                      style={{ color: styles.faintText }}
                    >
                      Decline
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        </>
      ) : null}
    </CrewPageShell>
  );
};

export default CrewView;
